import React from 'react';
import { colors, OverlaySpinner } from '@insurancemenu/imenu-components-lib';
import { useHistory } from 'react-router-dom';
import { FilePdf, FileXlsx, Mail } from '@insurancemenu/imenu-components-lib/dist/icons';
import { useProposalUrlParams } from 'global/hooks/useProposalUrlParams';
import { downloadByUrl } from '@insurancemenu/imenu-components-lib/dist/helpers/downloadByUrl';
import { observer } from 'mobx-react-lite';
import { useProposalReadyPageStore } from 'store/pages/proposal-generation/proposalReadyPageStore';
import SendOutEmailsModal from 'components/common/modals/SendOutEmailsModal';
import { ActionButtonStyled, ActionListStyled, ActionTileStyled, FooterStyled, WrapperStyled } from './styles';
import ProposalGenerationStepHeader from '../ProposalGenerationStepHeader';
import {
  BACK_TO_PROPOSAL_BTN_TEST_ID,
  DOWNLOAD_PDF_TILE_TEST_ID,
  DOWNLOAD_XLSX_TILE_TEST_ID,
  PROPOSAL_READY_INITIAL_SPINNER_TEST_ID,
  PROPOSAL_READY_SPINNER_TEST_ID,
  REGENERATE_PROPOSAL_BTN_TEST_ID,
  SHARE_PROPOSAL_FORM_ID,
  SHARE_PROPOSAL_TILE_TEST_ID,
} from '../constants';

const ReadyProposalActions: React.FC = observer(() => {
  const history = useHistory();
  const { url, proposalType } = useProposalUrlParams();
  const {
    isLoading,
    isInitialLoading,
    sendOutEmailsModalStore,
    isProposalConfirmed,
    pdfFilePath,
    xlsxFilePath,
    redirectBack,
  } = useProposalReadyPageStore();

  function initiateRegenerateFlow(): void {
    history.push(`/generate-proposal/choose-the-way/${url}&regenerateFlow=true&proposalType=${proposalType}`);
  }

  function onDownloadPdf(): void {
    if (pdfFilePath) {
      downloadByUrl(pdfFilePath);
    }
  }

  function onDownloadXlsx(): void {
    if (xlsxFilePath) {
      downloadByUrl(xlsxFilePath);
    }
  }

  return (
    <WrapperStyled>
      {isInitialLoading ? (
        <OverlaySpinner display data-test-id={PROPOSAL_READY_INITIAL_SPINNER_TEST_ID} />
      ) : (
        <>
          <ProposalGenerationStepHeader
            title="Your Proposal is Ready!"
            info="You can download or share your proposal anytime."
          />
          <ActionListStyled>
            {pdfFilePath !== null && (
              <ActionTileStyled
                icon={<FilePdf width="5.4rem" color={colors.red[600]} />}
                actionName="Download File"
                action={onDownloadPdf}
                message="Export the proposal in PDF format for easy viewing and printing"
                dataTestId={DOWNLOAD_PDF_TILE_TEST_ID}
              />
            )}
            {xlsxFilePath !== null && (
              <ActionTileStyled
                icon={<FileXlsx width="5.4rem" color={colors.green[600]} />}
                actionName="Download File"
                action={onDownloadXlsx}
                message="Export the proposal data in Excel format for detailed analysis"
                dataTestId={DOWNLOAD_XLSX_TILE_TEST_ID}
              />
            )}
            <ActionTileStyled
              icon={<Mail width="5.4rem" color={colors.blue[600]} />}
              actionName="Share Proposal"
              action={sendOutEmailsModalStore.open}
              message="Send the proposal to selected recipient via email"
              dataTestId={SHARE_PROPOSAL_TILE_TEST_ID}
            />
          </ActionListStyled>
          <FooterStyled>
            <ActionButtonStyled
              type="outlined"
              size="large"
              onClick={redirectBack}
              data-test-id={BACK_TO_PROPOSAL_BTN_TEST_ID}
            >
              Back to proposal page
            </ActionButtonStyled>
            {!isProposalConfirmed && (
              <ActionButtonStyled
                color="primary"
                size="large"
                onClick={initiateRegenerateFlow}
                data-test-id={REGENERATE_PROPOSAL_BTN_TEST_ID}
              >
                Customize Proposal
              </ActionButtonStyled>
            )}
          </FooterStyled>
          <OverlaySpinner display={isLoading} data-test-id={PROPOSAL_READY_SPINNER_TEST_ID} />
          <SendOutEmailsModal
            title="Share proposal"
            subtitle="Please input the email you want to share the proposal with."
            modalStore={sendOutEmailsModalStore}
            formId={SHARE_PROPOSAL_FORM_ID}
            withMessage
          />
        </>
      )}
    </WrapperStyled>
  );
});

export default ReadyProposalActions;
